/**
 * Pool module public API — Pool content fetch.
 *
 * Uses the shared pool client in `core/config/client.ts`; the website payload
 * from the init fetch is reused (normalized once, never re-fetched).
 *
 * Argument resolution follows the uniform precedence:
 * URL `kz_*` params > explicit method args > resolved init options.
 */

import { AwaitFor } from '../../core/events/awaiter';
import {
  fetchPostPayload,
  fetchWebsitePayload,
  normalizePoolLocale,
} from '../../core/config/client';
import {
  QueryParam,
  resolveDebugFromUrl,
  resolveDomainFromUrl,
  resolveIncludeDraftsFromUrl,
  resolveLocaleFromUrl,
  resolvePostIdFromUrl,
  resolveCountryFromUrl,
} from '../../core/queryParams';
import { resolveRenderCallback } from '../../core/options';
import { hideForFetch, showForFetch } from '../../core/loader';
import type { SdkContext } from '../types';
import { normalizePost, normalizeWebsite, type NormalizePostOptions } from './normalize';
import { POOL_EVENTS, buildContentFetchParams, type ContentFetchPayload, type PoolEventType } from './events';
import type { SdkPost, SdkWebsite } from './types';

export interface FetchWebsiteApiArgs {
  /** Defaults to the init domain; a different domain triggers a fresh fetch. */
  domain?: string;
  apiBaseUrl?: string;
  signal?: AbortSignal;
}

export interface FetchPostApiArgs {
  domain?: string;
  id?: string | number;
  locale?: string;
  includeDrafts?: boolean;
  apiBaseUrl?: string;
  signal?: AbortSignal;
  /** Forwarded to normalizePost for deterministic date-macro tests. */
  now?: Date;
}

export interface PoolModuleApi {
  readonly activePost: SdkPost | null;
  readonly postIdQueryParam: QueryParam.PostId;
  readonly localeQueryParam: QueryParam.Locale;
  readonly debugQueryParam: QueryParam.Debug;
  readonly includeDraftsQueryParam: QueryParam.IncludeDrafts;
  readonly domainQueryParam: QueryParam.Domain;
  resolvePostIdFromUrl(search?: string, fallback?: string | number): string;
  resolveLocaleFromUrl(search?: string, fallback?: string): string;
  resolveDebugFromUrl(fallback?: boolean): boolean | undefined;
  resolveIncludeDraftsFromUrl(fallback?: boolean): boolean | undefined;
  resolveDomainFromUrl(search?: string, fallback?: string): string;
  fetchWebsite(args?: FetchWebsiteApiArgs): Promise<SdkWebsite>;
  fetchPost(args?: FetchPostApiArgs): Promise<SdkPost>;
}

export function createPoolModuleApi(ctx: SdkContext): PoolModuleApi {
  let activePost: SdkPost | null = null;
  let autoFetchAttempted = false;
  let cachedWebsite: SdkWebsite | null = null;

  const waitReady = (): Promise<void> => {
    return ctx.awaitFor(AwaitFor.READY).then(() => undefined);
  };

  const trackFetchEvent = (event: PoolEventType, payload: ContentFetchPayload): void => {
    ctx.track(event, buildContentFetchParams(payload));
  };

  /** Website slice from the init fetch — same bytes, normalized once. */
  const websiteFromInitPayload = (): SdkWebsite => {
    if (!cachedWebsite) {
      const payload = ctx.getWebsitePayload();
      if (!payload) {
        throw new Error(
          '[kueez-sdk/pool] no website payload — this bundle was built with a sealed config and never called the pool.',
        );
      }
      cachedWebsite = normalizeWebsite(payload);
    }
    return cachedWebsite;
  };

  const resolveDomain = (...candidates: Array<string | undefined>): string =>
    (resolveDomainFromUrl() || candidates.find((c) => Boolean(c?.trim())) || '').trim();

  const resolveFetchPostArgs = (args: FetchPostApiArgs) => {
    const options = ctx.getOptions();
    const domain = resolveDomain(args.domain, ctx.getConfig().website.domain);
    const id = resolvePostIdFromUrl() || args.id || options.postId;
    const locale = normalizePoolLocale(
      resolveLocaleFromUrl() || args.locale || options.defaultLocale,
    );
    if (!domain) throw new Error('fetchPost: domain required');
    if (id === undefined || id === null || (typeof id === 'string' && !id.trim())) {
      throw new Error('fetchPost: id required');
    }
    return {
      domain,
      id,
      locale,
      apiBaseUrl: args.apiBaseUrl?.trim() || options.apiBaseUrl,
      signal: args.signal,
      includeDrafts:
        resolveIncludeDraftsFromUrl() ?? args.includeDrafts ?? options.includeDrafts,
      now: args.now,
    };
  };

  const runRenderCallback = (post: SdkPost): void => {
    const render = resolveRenderCallback();
    if (!render) return;
    try {
      render(post);
    } catch {
      /* render callback is host code; keep SDK lifecycle unblocked */
    }
  };

  /**
   * Shared content-fetch lifecycle: READY gate, loader, timed
   * start/success/error events, and endContentFetch.
   */
  const runContentFetch = async <T>(opts: {
    eventBase: ContentFetchPayload;
    fetch: () => Promise<T>;
    onError?: () => void;
    endFetch: () => void;
  }): Promise<T> => {
    ctx.beginContentFetch();
    try {
      await waitReady();
    } catch (err) {
      ctx.endContentFetch();
      throw err;
    }

    showForFetch();
    trackFetchEvent(POOL_EVENTS.contentFetchStart, opts.eventBase);
    const start = Date.now();
    try {
      const result = await opts.fetch();
      trackFetchEvent(POOL_EVENTS.contentFetchSuccess, {
        ...opts.eventBase,
        ms: Date.now() - start,
      });
      return result;
    } catch (err) {
      opts.onError?.();
      trackFetchEvent(POOL_EVENTS.contentFetchError, {
        ...opts.eventBase,
        ms: Date.now() - start,
        error: errMsg(err),
      });
      throw err;
    } finally {
      hideForFetch();
      opts.endFetch();
    }
  };

  const api: PoolModuleApi = {
    get activePost() {
      return activePost;
    },
    postIdQueryParam: QueryParam.PostId,
    localeQueryParam: QueryParam.Locale,
    debugQueryParam: QueryParam.Debug,
    includeDraftsQueryParam: QueryParam.IncludeDrafts,
    domainQueryParam: QueryParam.Domain,
    resolvePostIdFromUrl,
    resolveLocaleFromUrl,
    resolveDebugFromUrl,
    resolveIncludeDraftsFromUrl,
    resolveDomainFromUrl,

    async fetchWebsite(args = {}) {
      const initDomain = ctx.getConfig().website.domain;
      const requestedDomain = resolveDomain(args.domain, ctx.getOptions().domain);
      const canReuseInitPayload =
        !args.apiBaseUrl &&
        (requestedDomain === initDomain || requestedDomain === ctx.getOptions().domain);
      if (canReuseInitPayload) {
        return websiteFromInitPayload();
      }

      return runContentFetch({
        eventBase: { kind: 'website', domain: requestedDomain },
        endFetch: () => ctx.endContentFetch(),
        fetch: async () => {
          const payload = await fetchWebsitePayload({
            domain: requestedDomain,
            apiBaseUrl: args.apiBaseUrl?.trim() || ctx.getOptions().apiBaseUrl,
            signal: args.signal,
            country: resolveCountryFromUrl() || undefined,
          });
          return normalizeWebsite(payload);
        },
      });
    },

    async fetchPost(args = {}) {
      const resolved = resolveFetchPostArgs(args);
      const eventBase: ContentFetchPayload = {
        kind: 'post',
        domain: resolved.domain,
        locale: resolved.locale,
        id: String(resolved.id),
      };

      return runContentFetch({
        eventBase,
        onError: () => {
          activePost = null;
        },
        endFetch: () => ctx.endContentFetch(activePost),
        fetch: async () => {
          const payload = await fetchPostPayload({
            domain: resolved.domain,
            id: resolved.id,
            locale: resolved.locale,
            includeDrafts: resolved.includeDrafts,
            apiBaseUrl: resolved.apiBaseUrl,
            signal: resolved.signal,
          });
          const normalizeOpts: NormalizePostOptions = {};
          if (resolved.now) normalizeOpts.now = resolved.now;
          const post = normalizePost(payload, normalizeOpts);
          activePost = post;
          // Served locale may differ from the requested one (pool fallback);
          // analytics `lo=` must follow what was actually served.
          ctx.setContentLocale(post.locale || resolved.locale);
          return post;
        },
      });
    },
  };

  const autoFetchPost = async (): Promise<void> => {
    if (autoFetchAttempted) return;
    autoFetchAttempted = true;
    if (ctx.contentFetchInFlight() > 0 || activePost) return;
    const id = resolvePostIdFromUrl() || ctx.getOptions().postId || '';
    if (!id) {
      activePost = null;
      ctx.completeAwait(AwaitFor.CONTENT, null);
      return;
    }
    try {
      const post = await api.fetchPost({});
      runRenderCallback(post);
    } catch {
      /* fetchPost already emitted error events and completed CONTENT(null). */
    }
  };

  void ctx.awaitFor(AwaitFor.READY).then(autoFetchPost);

  return api;
}

function errMsg(err: unknown): string {
  if (err instanceof Error) return err.message.slice(0, 200);
  if (typeof err === 'string') return err.slice(0, 200);
  return 'unknown';
}
